import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const slides = [
  {
    src: "https://customer-assets.emergentagent.com/job_feature-branch-55/artifacts/xrlh1jcr_Cam%2003%20PURNIMA_EXT_NK_005_Opt%2001%20copy.jpg",
    bg: "#dce8f0",
    dark: false,
    project: "Poornima Heights",
    location: "Mumbai",
    tagline: "Modern Living with Premium Retail & Elegant Design",
    slug: "poornima-heights",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_feature-branch-55/artifacts/ifcvkn1f_gayatri_new2.jpeg",
    bg: "#0e1420",
    dark: true,
    project: "Gayatri Darshan",
    location: "Mulund East, Mumbai",
    tagline: "Experience the Luxurious Rise for an Excellent Lifestyle",
    slug: "gayatri-darshan",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_feature-branch-55/artifacts/wkuekyhu_Gemini_Generated_Image_ewsxdxewsxdxewsx.png",
    bg: "#e8f0f5",
    dark: false,
    project: "Vamantas",
    location: "Mulund East, Mumbai",
    tagline: "Modern Living with Green Spaces & Premium Retail",
    slug: "vamantas",
  },
];

export default function UpcomingSlideshow() {
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);

  const goTo = useCallback((index) => {
    setFading(true);
    setTimeout(() => {
      setCurrent(index);
      setFading(false);
    }, 350);
  }, []);

  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + slides.length) % slides.length), [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = slides[current];

  return (
    <section
      className="relative w-full overflow-hidden transition-colors duration-500"
      style={{ height: "92vh", minHeight: "540px", background: slide.bg }}
    >
      {/* Slide image */}
      <img
        key={current}
        src={slide.src}
        alt={`${slide.project} preview ${current + 1}`}
        className="w-full h-full object-contain"
        style={{ opacity: fading ? 0 : 1, transition: "opacity 0.35s ease-in-out" }}
      />

      {/* ── TEXT OVERLAY ── */}
      <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none">
        {slide.dark && <div className="absolute inset-0 h-48 bg-gradient-to-b from-black/55 to-transparent" />}

        <div className="relative px-6 md:px-12 pt-6 md:pt-8 flex items-start justify-between">
          {/* Left: Project label */}
          <div>
            <h2
              className="text-2xl md:text-4xl font-bold leading-tight"
              style={{
                color: slide.dark ? "#ffffff" : "#1a1612",
                textShadow: slide.dark ? "0 2px 12px rgba(0,0,0,0.7)" : "none",
              }}
            >
              {slide.project}
            </h2>
            <p
              className="text-sm md:text-base font-medium mt-0.5"
              style={{ color: slide.dark ? "rgba(255,255,255,0.80)" : "rgba(0,0,0,0.55)" }}
            >
              {slide.location}
            </p>
          </div>

          {/* Right: CTA */}
          <Link
            to={`/projects/${slide.slug}`}
            className="pointer-events-auto hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ background: "hsl(40 90% 52%)", color: "#1a1a1a", boxShadow: "0 2px 12px rgba(0,0,0,0.25)" }}
            data-testid="slideshow-view-details"
          >
            View Details <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      {/* Bottom tagline + counter */}
      <div className="absolute bottom-14 left-0 right-0 z-10 px-6 md:px-12 pointer-events-none">
        <div className="flex items-end justify-between">
          <p
            className="text-xs md:text-sm font-medium hidden sm:block"
            style={{ color: slide.dark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.50)" }}
          >
            {slide.tagline}
          </p>
          <span
            className="text-xs font-semibold px-2.5 py-1 rounded-full"
            style={{
              background: slide.dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.10)",
              color: slide.dark ? "#fff" : "#333",
              backdropFilter: "blur(4px)",
            }}
          >
            {current + 1} / {slides.length}
          </span>
        </div>
      </div>

      {/* Arrows */}
      <button
        data-testid="slideshow-prev"
        onClick={prev}
        className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full flex items-center justify-center transition-all"
        style={{
          background: slide.dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.10)",
          backdropFilter: "blur(6px)",
          border: slide.dark ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(0,0,0,0.12)",
          color: slide.dark ? "#fff" : "#333",
        }}
      >
        <ChevronLeft size={22} />
      </button>
      <button
        data-testid="slideshow-next"
        onClick={next}
        className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full flex items-center justify-center transition-all"
        style={{
          background: slide.dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.10)",
          backdropFilter: "blur(6px)",
          border: slide.dark ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(0,0,0,0.12)",
          color: slide.dark ? "#fff" : "#333",
        }}
      >
        <ChevronRight size={22} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 flex gap-2.5" data-testid="slideshow-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            data-testid={`dot-${i}`}
            onClick={() => goTo(i)}
            className="rounded-full transition-all duration-300"
            style={{
              width: i === current ? "28px" : "10px",
              height: "10px",
              background: i === current ? "hsl(40 90% 52%)" : slide.dark ? "rgba(255,255,255,0.40)" : "rgba(0,0,0,0.22)",
            }}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 z-10" style={{ background: slide.dark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)" }}>
        <div
          key={`prog-${current}`}
          className="h-full"
          style={{ background: "hsl(40 90% 52%)", animation: "slideProgress 5s linear forwards" }}
        />
      </div>

      <style>{`@keyframes slideProgress { from { width: 0% } to { width: 100% } }`}</style>
    </section>
  );
}
